import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Collapse,
  IconButton,
  Fab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  ExitToApp as ExitToAppIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { adminGroupsAPI, presenceAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface PresenceStatus {
  is_checked_in: boolean;
  last_check_in: string | null;
  last_check_out: string | null;
  work_hours_today: number;
  current_presence_id?: number;
  status?: string;
  simplified_mode?: boolean;
}

interface LocationData {
  latitude: number;
  longitude: number;
  address?: string;
  error?: string;
}

const MobilePresence: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [status, setStatus] = useState<PresenceStatus | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [location, setLocation] = useState<LocationData | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isInAdminManagedGroup, setIsInAdminManagedGroup] = useState(false);

  // Carica lo stato iniziale
  useEffect(() => {
    loadStatus();
    checkAdminManagedGroup();
  }, []);

  // Auto-refresh ogni 30 secondi se attivo
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadStatus();
    }, 30000); // 30 secondi

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadStatus = async () => {
    try {
      setLoadingStatus(true);
      const statusData = await presenceAPI.getStatus();
      setStatus(statusData);
      setError('');
    } catch (err: any) {
      setError('Errore nel caricamento dello stato');
    } finally {
      setLoadingStatus(false);
    }
  };

  const checkAdminManagedGroup = async () => {
    try {
      const response = await adminGroupsAPI.isEmployeeInAdminManagedGroup();
      setIsInAdminManagedGroup(response.is_in_admin_managed_group || false);
    } catch (err) {
      setIsInAdminManagedGroup(false);
    }
  };

  const getCurrentLocation = async (): Promise<LocationData | null> => {
    if (!navigator.geolocation) {
      return { latitude: 0, longitude: 0, error: 'Geolocalizzazione non supportata' };
    }

    return new Promise((resolve) => {
      setLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          // Opzionale: reverse geocoding per ottenere l'indirizzo
          let address = '';
          try {
            // Usa un servizio di geocoding (es. Nominatim OpenStreetMap)
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
            );
            const data = await response.json();
            address = data.display_name || '';
          } catch (err) {
            // Errore reverse geocoding
          }

          setLocationLoading(false);
          resolve({ latitude, longitude, address });
        },
        (error) => {
          setLocationLoading(false);
          resolve({
            latitude: 0,
            longitude: 0,
            error: `Errore geolocalizzazione: ${error.message}`,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  const handleCheckIn = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Richiedi geolocalizzazione (opzionale)
      let locationData: LocationData | null = null;
      try {
        locationData = await getCurrentLocation();
        if (locationData?.error) {
          // Continua comunque senza geolocalizzazione
        }
      } catch (err) {
        // Continua comunque
      }

      // Aggiungi info geolocalizzazione alle note se disponibile
      let finalNotes = notes;
      if (locationData && !locationData.error && locationData.address) {
        finalNotes = notes
          ? `${notes}\n[Posizione: ${locationData.address}]`
          : `[Posizione: ${locationData.address}]`;
      }

      await presenceAPI.checkIn(finalNotes);
      setSuccess('Check-in effettuato con successo!');
      setNotes('');
      setLocation(null);
      
      // Feedback visivo
      if ('vibrate' in navigator) {
        navigator.vibrate(200); // Vibrazione di 200ms
      }

      await loadStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante il check-in');
      if ('vibrate' in navigator) {
        navigator.vibrate([100, 50, 100]); // Vibrazione di errore
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await presenceAPI.checkOut(notes);
      setSuccess('Check-out effettuato con successo!');
      setNotes('');

      // Feedback visivo
      if ('vibrate' in navigator) {
        navigator.vibrate(200);
      }

      await loadStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante il check-out');
      if ('vibrate' in navigator) {
        navigator.vibrate([100, 50, 100]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterDay = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await presenceAPI.registerDay(notes);
      setSuccess('Presenza di oggi registrata con successo!');
      setNotes('');

      if ('vibrate' in navigator) {
        navigator.vibrate(200);
      }

      await loadStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante la registrazione della presenza');
      if ('vibrate' in navigator) {
        navigator.vibrate([100, 50, 100]);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const formatHours = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loadingStatus) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        p={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Caricamento...
        </Typography>
      </Box>
    );
  }

  const isCheckedIn = status?.is_checked_in || false;
  const hasPendingCheckout = status?.status === 'pending_checkout';
  const pendingCheckoutText = status?.last_check_in
    ? `Risulta una timbratura aperta dal ${formatDate(status.last_check_in)} alle ${formatTime(status.last_check_in)}. Contatta l'amministratore per la chiusura.`
    : 'Risulta una timbratura aperta di un giorno precedente. Contatta l\'amministratore per la chiusura.';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        pb: 10, // Spazio per il pulsante fisso
      }}
    >
      {/* Header semplificato */}
      <Box
        sx={{
          backgroundColor: '#0d47a1',
          color: 'white',
          p: 2,
          position: 'relative',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight="bold">
              Timbratura Presenze
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
              {user?.nome} {user?.cognome}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              logout();
              navigate('/login');
            }}
            sx={{
              color: 'white',
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            aria-label="Logout"
          >
            <ExitToAppIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* Messaggi di errore/successo */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Card Stato Attuale */}
        <Card
          sx={{
            mb: 3,
            backgroundColor: hasPendingCheckout ? '#fff4e5' : isCheckedIn ? '#e8f5e9' : '#fff3e0',
            border: `2px solid ${hasPendingCheckout ? '#ed6c02' : isCheckedIn ? '#4caf50' : '#ff9800'}`,
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Stato Attuale
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setAutoRefresh(!autoRefresh);
                  loadStatus();
                }}
                disabled={loadingStatus}
              >
                <RefreshIcon />
              </IconButton>
            </Box>

            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Chip
                label={hasPendingCheckout ? 'VERIFICA RICHIESTA' : isCheckedIn ? 'IN LAVORO' : 'NON IN LAVORO'}
                color={hasPendingCheckout ? 'warning' : isCheckedIn ? 'success' : 'default'}
                icon={isCheckedIn ? <LoginIcon /> : <LogoutIcon />}
                sx={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  height: '40px',
                  px: 2,
                }}
              />
            </Box>

            {status?.last_check_in && (
              <Box mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Ultimo check-in:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatDate(status.last_check_in)} alle {formatTime(status.last_check_in)}
                </Typography>
              </Box>
            )}

            {status?.last_check_out && (
              <Box mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Ultimo check-out:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatDate(status.last_check_out)} alle {formatTime(status.last_check_out)}
                </Typography>
              </Box>
            )}

            {isCheckedIn && status?.work_hours_today !== undefined && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Ore lavorate oggi:
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {formatHours(status.work_hours_today)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {hasPendingCheckout && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {pendingCheckoutText}
          </Alert>
        )}

        {/* Campo Note (collassabile) */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setNotesExpanded(!notesExpanded)}
              sx={{ cursor: 'pointer' }}
            >
              <Typography variant="h6">Note (opzionale)</Typography>
              <IconButton size="small">
                {notesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={notesExpanded}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Aggiungi una nota..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mt: 2 }}
                disabled={loading}
              />
            </Collapse>
          </CardContent>
        </Card>

        {/* Link rapido alla storia */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AccessTimeIcon />}
          onClick={() => navigate('/history')}
          sx={{ mb: 2 }}
        >
          Vedi Storia Presenze
        </Button>

        {isInAdminManagedGroup && (
          <Alert severity="info">
            Il tuo cartellino giornaliero viene compilato dall'amministratore del gruppo. Da qui puoi solo consultare lo stato e lo storico.
          </Alert>
        )}
      </Box>

      {/* Pulsante principale fisso in basso */}
      {!isInAdminManagedGroup && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            backgroundColor: 'white',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            zIndex: 1000,
          }}
        >
          {hasPendingCheckout ? (
            <Button
              fullWidth
              variant="contained"
              color="warning"
              size="large"
              disabled
              sx={{
                height: '60px',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                textTransform: 'none',
              }}
            >
              VERIFICA RICHIESTA
            </Button>
          ) : status?.simplified_mode ? (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AccessTimeIcon />}
              onClick={handleRegisterDay}
              disabled={loading || loadingStatus || status?.status === 'completed'}
              sx={{
                height: '60px',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                textTransform: 'none',
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : status?.status === 'completed' ? (
                'PRESENZA REGISTRATA'
              ) : (
                'REGISTRA PRESENZA'
              )}
            </Button>
          ) : !isCheckedIn ? (
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="large"
              startIcon={<LoginIcon />}
              onClick={handleCheckIn}
              disabled={loading || loadingStatus || status?.status === 'completed'}
              sx={{
                height: '60px',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                textTransform: 'none',
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'ENTRATA'}
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              color="error"
              size="large"
              startIcon={<LogoutIcon />}
              onClick={handleCheckOut}
              disabled={loading || loadingStatus}
              sx={{
                height: '60px',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                textTransform: 'none',
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'USCITA'}
            </Button>
          )}
        </Box>
      )}

      {/* Indicatore geolocalizzazione */}
      {locationLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 2,
            zIndex: 2000,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} color="inherit" />
            <Typography variant="body2">Rilevamento posizione...</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MobilePresence;

